/**
 * Списание и возврат аккаунта (KAN-617). "Списать" с подтверждением,
 * "Вернуть" сразу. Работает для любого держателя, кроме основного аккаунта.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/telegram/ConfirmDialog';
import { Button } from '@/components/ui/button';
import {
  DECOMMISSION_CONFIRM,
  decommissionErrorMessage,
  decommissionNotice,
} from '@/lib/telegramAccountActions';
import { EXPENSES_QUERY_KEY } from '@/services/api/queryKeys';
import { telegramAccountsApi } from '@/services/api/telegramAccounts';
import type { TelegramAccount } from '@/types/telegramAccount';

const BTN = 'h-7 px-2 text-xs';

export function DecommissionActions({
  account,
}: {
  account: TelegramAccount;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const key = `${account.operator_id}-${account.account_id}`;
  const [open, setOpen] = useState(false);

  const m = useMutation({
    mutationFn: (decommissioned: boolean) =>
      telegramAccountsApi.updateAccount(
        account.account_id,
        { decommissioned },
        account.operator_id
      ),
    onSuccess: (result, decommissioned) => {
      const notice = decommissionNotice(decommissioned, result);
      toast[notice.kind](notice.text);
      void queryClient.invalidateQueries({ queryKey: ['telegram-accounts'] });
      void queryClient.invalidateQueries({ queryKey: EXPENSES_QUERY_KEY }); // KAN-735: списания
    },
    onError: (err) => toast.error(decommissionErrorMessage(err)),
  });

  if (account.account_id === 'primary') return null;

  if (account.decommissioned) {
    return (
      <div className="mt-1.5">
        <Button
          variant="outline"
          size="sm"
          className={BTN}
          disabled={m.isPending}
          data-testid={`restore-${key}`}
          onClick={() => m.mutate(false)}
        >
          Вернуть
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-1.5">
      <Button
        variant="outline"
        size="sm"
        className={`${BTN} text-red-700`}
        disabled={m.isPending}
        data-testid={`decommission-${key}`}
        onClick={() => setOpen(true)}
      >
        Списать
      </Button>
      <ConfirmDialog
        testId={`decommission-dialog-${key}`}
        open={open}
        onOpenChange={setOpen}
        view={DECOMMISSION_CONFIRM}
        onConfirm={() => m.mutate(true)}
      />
    </div>
  );
}
